import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Divider, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
interface RecordUploadCardProps {
  onClose: () => void;
}
const RecordUploadCard: React.FC<RecordUploadCardProps> = ({
  onClose
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'initial' | 'preview' | 'success'>('initial');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const theme = useTheme();
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx')) {
        setFile(droppedFile);
        // In a real app, you would parse the Excel file here
        // For now, let's simulate a preview with mock data
        simulateFileUpload(droppedFile);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.xlsx')) {
        setFile(selectedFile);
        // In a real app, you would parse the Excel file here
        // For now, let's simulate a preview with mock data
        simulateFileUpload(selectedFile);
      }
    }
  };
  const simulateFileUpload = (file: File) => {
    // Simulate processing delay
    setTimeout(() => {
      // Mock preview data
      const mockData = [{
        fullName: 'Sarah Johnson',
        username: 'sarah.johnson',
        email: 'sarah.johnson@ecobank.com.gh',
        mobile: '+233 20 123 4567',
        branchCode: 'GH-ACC-001',
        status: 'new'
      }, {
        fullName: 'Kofi Mensah',
        username: 'kofi.mensah',
        email: 'kofi.mensah@ecobank.com.ng',
        mobile: '+234 81 234 5678',
        branchCode: 'NG-LAG-001',
        status: 'update'
      }, {
        fullName: 'Amina Hassan',
        username: 'amina.hassan',
        email: 'amina.hassan@ecobank.sn',
        mobile: '+221 77 345 6789',
        branchCode: 'SN-DKR-001',
        status: 'error',
        error: 'Email already exists'
      }, {
        fullName: 'James Okafor',
        username: 'james.okafor',
        email: 'james.okafor@ecobank.ug',
        mobile: '+256 70 456 7890',
        branchCode: 'UG-KLA-001',
        status: 'new'
      }, {
        fullName: 'Grace Kipchoge',
        username: 'grace.kipchoge',
        email: 'grace.kipchoge@ecobank.ke',
        mobile: '+254 722 567 890',
        branchCode: 'KE-NBI-001',
        status: 'update'
      }];
      setPreviewData(mockData);
      setUploadStatus('preview');
    }, 1000);
  };
  const handleConfirmImport = () => {
    // In a real app, this would submit the data to the backend
    setUploadStatus('success');
    // After successful import, you might want to refresh the users table
    // For now, just close the upload card after a delay
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  // Count statistics
  const newCount = previewData.filter(row => row.status === 'new').length;
  const updateCount = previewData.filter(row => row.status === 'update').length;
  const errorCount = previewData.filter(row => row.status === 'error').length;
  return <Card elevation={0} sx={{
    mb: 3,
    borderRadius: 2,
    border: '1px solid #E5E7EB'
  }} data-id="element-1569">
      <CardContent data-id="element-1570">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-1571">
          <Typography variant="h6" component="h2" data-id="element-1572">
            {uploadStatus === 'initial' ? 'Upload Records' : uploadStatus === 'preview' ? 'Preview Bulk Upload' : 'Bulk Upload Successful'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1573">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1574" />
          </IconButton>
        </Box>
        {uploadStatus === 'initial' && <Box sx={{
        border: `2px dashed ${isDragging ? theme.palette.primary.main : '#D1D5DB'}`,
        borderRadius: 1,
        p: 3,
        backgroundColor: isDragging ? 'rgba(14, 165, 233, 0.04)' : '#FFFFFF',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById('file-upload')?.click()} data-id="element-1575">
            <input id="file-upload" type="file" accept=".xlsx" onChange={handleFileChange} style={{
          display: 'none'
        }} data-id="element-1576" />
            <Icon icon="lucide:upload" width={32} height={32} color="#0EA5E9" style={{
          margin: '0 auto 16px'
        }} data-id="element-1577" />
            <Typography variant="body1" sx={{
          mb: 1
        }} data-id="element-1578">
              Drag and drop your XLSX file here, or{' '}
              <span style={{
            color: '#0EA5E9',
            fontWeight: 500
          }} data-id="element-1579">
                browse
              </span>
            </Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-1580">
              Only Excel (XLSX) files are supported
            </Typography>
          </Box>}
        {uploadStatus === 'preview' && <>
            <Box sx={{
          mb: 3
        }} data-id="element-1581">
              <Typography variant="subtitle2" sx={{
            mb: 1
          }} data-id="element-1582">
                File:{' '}
                <span style={{
              fontWeight: 400
            }} data-id="element-1583">
                  {file?.name}
                </span>
              </Typography>
              <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 2
          }} data-id="element-1584">
                <Chip icon={<Icon icon="lucide:check-circle" width={16} height={16} data-id="element-1586" />} label={`${newCount} New`} sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              '.MuiChip-icon': {
                color: '#10B981'
              }
            }} data-id="element-1585" />
                <Chip icon={<Icon icon="lucide:file-text" width={16} height={16} data-id="element-1588" />} label={`${updateCount} Updates`} sx={{
              backgroundColor: 'rgba(14, 165, 233, 0.1)',
              color: '#0EA5E9',
              '.MuiChip-icon': {
                color: '#0EA5E9'
              }
            }} data-id="element-1587" />
                {errorCount > 0 && <Chip icon={<Icon icon="lucide:alert-circle" width={16} height={16} data-id="element-1590" />} label={`${errorCount} Errors`} sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              '.MuiChip-icon': {
                color: '#EF4444'
              }
            }} data-id="element-1589" />}
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{
          border: '1px solid #E5E7EB',
          mb: 3
        }} data-id="element-1591">
              <Table size="small" data-id="element-1592">
                <TableHead data-id="element-1593">
                  <TableRow data-id="element-1594">
                    <TableCell data-id="element-1595">Status</TableCell>
                    <TableCell data-id="element-1596">Full Name</TableCell>
                    <TableCell data-id="element-1597">Username</TableCell>
                    <TableCell data-id="element-1598">Email Address</TableCell>
                    <TableCell data-id="element-1599">Mobile</TableCell>
                    <TableCell data-id="element-1600">Branch Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-1601">
                  {previewData.map((row, index) => <TableRow key={index} sx={{
                backgroundColor: row.status === 'error' ? 'rgba(239, 68, 68, 0.05)' : 'inherit'
              }} data-id="element-1602">
                      <TableCell data-id="element-1603">
                        {row.status === 'new' && <Chip label="New" size="small" sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981',
                    height: 24
                  }} data-id="element-1604" />}
                        {row.status === 'update' && <Chip label="Update" size="small" sx={{
                    backgroundColor: 'rgba(14, 165, 233, 0.1)',
                    color: '#0EA5E9',
                    height: 24
                  }} data-id="element-1605" />}
                        {row.status === 'error' && <Chip label="Error" size="small" sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#EF4444',
                    height: 24
                  }} data-id="element-1606" />}
                      </TableCell>
                      <TableCell data-id="element-1607">{row.fullName}</TableCell>
                      <TableCell data-id="element-1608">{row.username}</TableCell>
                      <TableCell data-id="element-1609">{row.email}</TableCell>
                      <TableCell data-id="element-1610">{row.mobile}</TableCell>
                      <TableCell data-id="element-1611">{row.branchCode}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
            {errorCount > 0 && <Box sx={{
          mb: 3,
          p: 2,
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          borderRadius: 1
        }} data-id="element-1612">
                <Typography variant="body2" color="error" sx={{
            display: 'flex',
            alignItems: 'center'
          }} data-id="element-1613">
                  <Icon icon="lucide:alert-circle" width={16} height={16} style={{
              marginRight: 8
            }} data-id="element-1614" />
                  Please fix the errors before uploading
                </Typography>
              </Box>}
          </>}
        {uploadStatus === 'success' && <Box sx={{
        textAlign: 'center',
        py: 3
      }} data-id="element-1615">
            <Icon icon="lucide:check-circle" width={48} height={48} color="#10B981" style={{
          margin: '0 auto 16px'
        }} data-id="element-1616" />
            <Typography variant="h6" data-id="element-1617">Bulk Upload Successful</Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-1618">
              {newCount} new records and {updateCount} updates have been
              processed
            </Typography>
          </Box>}
        <Divider sx={{
        my: 2
      }} data-id="element-1619" />
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1620">
          <Button variant="outlined" onClick={onClose} data-id="element-1621">
            Cancel
          </Button>
          {uploadStatus === 'preview' && <Button variant="contained" onClick={handleConfirmImport} disabled={errorCount > 0} data-id="element-1622">
              Confirm Upload
            </Button>}
        </Box>
      </CardContent>
    </Card>;
};
export default RecordUploadCard;